// Google Cast: plays the book on a Cast device (Chromecast, Google TV, Nest
// speakers) from Chrome on Android and the desktop. The Cast Web Sender SDK
// (Google's script, loaded only in Chromium browsers) finds the devices and
// opens the session; the Default Media Receiver plays Audiobookshelf's own
// audio files and shows the cover, title and author on the TV. The engine
// hands the book over through `RemotePlayback` (remote.ts) and takes it back
// when the session ends. AirPlay (Safari) is `airplay.ts`.

import { audioFilePath, getItem } from '../api/abs';
import { ensureFreshToken, mediaUrl, tokenExpiresAt } from '../api/client';
import { getSession } from '../api/session';
import type { AudioFile } from '../api/types';
import { platform } from '../lib/device';
import { createStore } from '../lib/store';
import type { RemoteBook, RemoteHost, RemotePlayback, RemoteStatus } from './remote';

export const CAST_SDK_URL = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
/** Speeds the Default Media Receiver plays; faster books play at 2x on the TV. */
export const CAST_MIN_RATE = 0.5;
export const CAST_MAX_RATE = 2;
/** A receiver that fails this many times in `CAST_RETRY_WINDOW_MS` reports the error instead of reloading. */
const CAST_MAX_RETRIES = 3;
const CAST_RETRY_WINDOW_MS = 10 * 60_000;
/** A seek or load the receiver hasn't confirmed by then stops standing in for its position. */
const TARGET_HOLD_MS = 8000;
/** A speed change waits this long for the slider to settle: the receiver reloads to change speed. */
const RATE_DEBOUNCE_MS = 700;
const MEDIA_NAMESPACE_TYPES = { BUFFERED: 'BUFFERED' } as const;

export type CastStatus =
  /** No Cast support here (Safari, Firefox, iOS, insecure origin) or the SDK failed to load. */
  | 'unsupported'
  /** The SDK is loading. */
  | 'loading'
  /** The SDK runs but sees no device on the network yet. */
  | 'noDevices'
  | 'available'
  | 'connecting'
  | 'connected';

export interface CastState {
  status: CastStatus;
  /** The connected (or connecting) device's name. */
  device: string | null;
}

export const castStore = createStore<CastState>({ status: 'unsupported', device: null });

// ---------------------------------------------------------------- SDK shapes
// The few parts of the Web Sender SDK this module uses (it ships no types).

type ChromeCast = any;
type CastFramework = any;
interface CastMedia {
  mediaSessionId: number;
  playerState: 'IDLE' | 'PLAYING' | 'PAUSED' | 'BUFFERING' | string;
  idleReason?: 'CANCELLED' | 'INTERRUPTED' | 'FINISHED' | 'ERROR' | string | null;
  playbackRate?: number;
  media?: { customData?: Partial<CastItemData> | null } | null;
  getEstimatedTime(): number;
  addUpdateListener(listener: (alive: boolean) => void): void;
  removeUpdateListener(listener: (alive: boolean) => void): void;
  play(request: unknown, ok: () => void, err: (e: unknown) => void): void;
  pause(request: unknown, ok: () => void, err: (e: unknown) => void): void;
  seek(request: unknown, ok: () => void, err: (e: unknown) => void): void;
  stop(request: unknown, ok: () => void, err: (e: unknown) => void): void;
}
interface CastSessionLike {
  getCastDevice(): { friendlyName?: string } | null;
  getMediaSession(): CastMedia | null;
  loadMedia(request: unknown): Promise<unknown>;
  addEventListener(type: string, listener: (e: any) => void): void;
  removeEventListener(type: string, listener: (e: any) => void): void;
}

/** Stored on every queue item, so a resumed session knows the book and where each file sits in it. */
export interface CastItemData {
  plumeItemId: string;
  /** Seconds from the start of the book to the start of this file. */
  startOffset: number;
  index: number;
  count: number;
}

declare global {
  interface Window {
    __onGCastApiAvailable?: (available: boolean, reason?: string) => void;
    cast?: { framework?: CastFramework };
    chrome?: { cast?: ChromeCast } & Record<string, unknown>;
  }
}

function sdk(): { cast: ChromeCast; framework: CastFramework } | null {
  const cast = window.chrome?.cast;
  const framework = window.cast?.framework;
  return cast && framework ? { cast, framework } : null;
}

// ---------------------------------------------------------------- pure helpers

/** A speed the receiver plays. */
export function castRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  return Math.min(CAST_MAX_RATE, Math.max(CAST_MIN_RATE, rate));
}

export interface CastFile {
  ino: string;
  duration: number;
  mimeType: string;
  startOffset: number;
}

/** The book's playable files in order, with where each starts in the book. */
export function castFiles(audioFiles: readonly AudioFile[]): CastFile[] {
  let offset = 0;
  return audioFiles
    .filter((f) => !f.exclude && !f.invalid)
    .sort((a, b) => a.index - b.index)
    .map((f) => {
      const file = { ino: f.ino, duration: f.duration, mimeType: f.mimeType || 'audio/mpeg', startOffset: offset };
      offset += f.duration;
      return file;
    });
}

/** The file holding book position `position`, and the offset into it. */
export function locateCastFile(files: readonly CastFile[], position: number): { index: number; offset: number } {
  let index = 0;
  for (let i = 0; i < files.length; i++) {
    if (position >= files[i]!.startOffset) index = i;
    else break;
  }
  const file = files[index];
  if (!file) return { index: 0, offset: 0 };
  return { index, offset: Math.max(0, Math.min(position - file.startOffset, Math.max(0, file.duration - 0.5))) };
}

/** An absolute URL the receiver can fetch (the app may talk to ABS through a same-origin path). */
export function absoluteUrl(url: string, base = location.href): string {
  return new URL(url, base).toString();
}

/**
 * What the receiver's state means for the engine. `last` = the current item
 * is the book's last file (a queue goes idle with FINISHED only at its end,
 * but a single file's FINISHED is the end too).
 */
export function castStatusOf(media: Pick<CastMedia, 'playerState' | 'idleReason'>, last: boolean): RemoteStatus | 'error' | 'gone' {
  switch (media.playerState) {
    case 'PLAYING':
      return { playing: true, buffering: false, ended: false, error: null };
    case 'BUFFERING':
      return { playing: false, buffering: true, ended: false, error: null };
    case 'PAUSED':
      return { playing: false, buffering: false, ended: false, error: null };
    case 'IDLE':
      if (media.idleReason === 'FINISHED') return last ? { playing: false, buffering: false, ended: true, error: null } : 'gone';
      if (media.idleReason === 'ERROR') return 'error';
      // Stopped from elsewhere (another sender, the TV), or no media yet.
      return 'gone';
    default:
      return { playing: false, buffering: false, ended: false, error: null };
  }
}

// ---------------------------------------------------------------- the receiver

/** One Cast session's playback, driven by the engine. */
export class CastRemote implements RemotePlayback {
  itemId: string | null = null;
  readonly deviceName: string;
  private media: CastMedia | null = null;
  private files: CastFile[] = [];
  private filesFor: string | null = null;
  private book: RemoteBook | null = null;
  private rate = 1;
  /** A seek or load in flight: the position it heads to stands in for the receiver's. */
  private target: { at: number; since: number } | null = null;
  private last = { position: 0, playing: false };
  private loadSerial = 0;
  private loading = false;
  /** Set while the listener stops casting: later receiver updates don't change the play state. */
  private ending = false;
  private retry = { count: 0, since: 0 };
  /** Expiry of the token inside the receiver's URLs. */
  private tokenExp: number | null = null;
  private rateTimer: ReturnType<typeof setTimeout> | null = null;
  /** The listener's play intent, which reloads (another file, speed, token, retry) keep. */
  private wantPlay = false;
  private readonly onUpdate = (alive: boolean) => this.mediaUpdated(alive);
  private readonly onMediaSession = (e: { mediaSession?: CastMedia }) => {
    if (e.mediaSession) this.attach(e.mediaSession, true);
  };

  constructor(
    private readonly session: CastSessionLike,
    private readonly host: RemoteHost,
    private readonly api: ChromeCast = window.chrome?.cast,
    framework: CastFramework = window.cast?.framework,
  ) {
    this.deviceName = session.getCastDevice()?.friendlyName || 'your TV';
    // A session resumed after a reload may already play one of our books: the engine follows it.
    const existing = session.getMediaSession();
    if (existing) this.attach(existing, false);
    const type = framework?.SessionEventType?.MEDIA_SESSION ?? 'mediasession';
    session.addEventListener(type, this.onMediaSession);
    this.mediaSessionType = type;
  }

  private mediaSessionType: string;

  dispose() {
    this.session.removeEventListener(this.mediaSessionType, this.onMediaSession);
    this.media?.removeUpdateListener(this.onUpdate);
    this.media = null;
    if (this.rateTimer) clearTimeout(this.rateTimer);
  }

  /** The listener is stopping the session: freeze the position and play state that playback comes back with. */
  beginEnding() {
    if (this.ending) return;
    this.last = { position: this.position(), playing: this.isPlaying() };
    this.ending = true;
  }

  /**
   * The session ended without the listener stopping it (the TV turned off or
   * left the network): playback comes back here paused, never out of the
   * phone's speaker by surprise.
   */
  markLost() {
    if (this.ending) return;
    this.last = { position: this.position(), playing: false };
    this.ending = true;
  }

  hasMedia(): boolean {
    return !!this.media && this.media.playerState !== 'IDLE' && this.ours(this.media);
  }

  /** The speed the receiver actually plays at (0.5–2x). */
  playbackRate(): number {
    return this.rate;
  }

  isPlaying(): boolean {
    if (this.ending) return this.last.playing;
    return !!this.media && this.media.playerState === 'PLAYING';
  }

  position(): number {
    if (this.target && (this.loading || Date.now() - this.target.since < TARGET_HOLD_MS)) return this.target.at;
    const m = this.media;
    if (this.ending || !m || !this.ours(m) || m.playerState === 'IDLE') return this.last.position;
    // Remembered, so a receiver that goes away (or reports nothing more) leaves a recent position behind.
    this.last.position = this.mediaPosition(m);
    return this.last.position;
  }

  adopt(book: RemoteBook): void {
    if (this.itemId !== book.itemId) return;
    // Seeks to another file, speed changes and error retries reload from this.
    this.book = book;
    this.rate = castRate(book.rate);
    this.wantPlay = this.isPlaying();
  }

  async load(book: RemoteBook): Promise<void> {
    const serial = ++this.loadSerial;
    this.book = book;
    this.itemId = book.itemId;
    this.rate = castRate(book.rate);
    this.wantPlay = book.autoplay;
    this.detach();
    this.loading = true;
    this.target = { at: book.startAt, since: Date.now() };
    this.last = { position: book.startAt, playing: false };
    try {
      const request = await this.buildRequest(book, book.startAt, book.autoplay);
      if (serial !== this.loadSerial) return;
      try {
        const code = await this.session.loadMedia(request);
        // Older SDKs resolve with an error code instead of rejecting.
        if (code) throw code;
      } catch (err) {
        if (serial !== this.loadSerial) return;
        console.warn('[cast] load failed', err);
        throw new Error(`${this.deviceName} couldn't play this book. Its audio format may not be supported there.`);
      }
      if (serial !== this.loadSerial) {
        // Stopped while the receiver was loading: stop what it just loaded.
        if (!this.book) this.session.getMediaSession()?.stop(new this.api.media.StopRequest(), noop, noop);
        return;
      }
      const media = this.session.getMediaSession();
      if (media) this.attach(media, false);
      // Paused while the receiver was loading.
      if (media && !this.wantPlay && book.autoplay) media.pause(new this.api.media.PauseRequest(), noop, noop);
    } finally {
      if (serial === this.loadSerial) {
        this.loading = false;
        if (this.target) this.target.since = Date.now();
      }
    }
    if (serial !== this.loadSerial) return;
    // A seek made while the receiver was loading.
    if (this.target && Math.abs(this.target.at - book.startAt) > 1) this.seek(this.target.at);
    else if (this.media) this.mediaUpdated(true);
  }

  play() {
    this.wantPlay = true;
    const m = this.media;
    if (!m || !this.hasMedia()) {
      this.reload(this.position(), true);
      return;
    }
    // A long pause may have outlived the token in the receiver's URLs; its next range request would fail.
    if (this.tokenStale()) {
      this.reload(this.position(), true);
      return;
    }
    m.play(new this.api.media.PlayRequest(), noop, (e) => this.commandFailed('play', e));
  }

  pause() {
    this.wantPlay = false;
    const m = this.media;
    if (!m || !this.hasMedia()) return;
    this.last = { position: this.position(), playing: false };
    m.pause(new this.api.media.PauseRequest(), noop, (e) => this.commandFailed('pause', e));
  }

  seek(position: number) {
    this.target = { at: position, since: Date.now() };
    const m = this.media;
    if (this.loading || !this.book) return; // the load applies it when done
    if (!m || !this.hasMedia()) return;
    const { index, offset } = locateCastFile(this.files, position);
    const current = Number(m.media?.customData?.index ?? 0);
    // A resumed session's file list may still be loading: reload, which fetches it.
    if (this.filesFor !== this.itemId || index !== current || this.tokenStale()) {
      // Another file (or a fresh token): reload the queue there.
      this.reload(position, this.wantPlay);
      return;
    }
    const request = new this.api.media.SeekRequest();
    request.currentTime = offset;
    m.seek(request, noop, (e) => this.commandFailed('seek', e));
  }

  setRate(rate: number) {
    const r = castRate(rate);
    if (r === this.rate) return;
    this.rate = r;
    if (this.rateTimer) clearTimeout(this.rateTimer);
    // The Default Media Receiver has no speed command for web senders: the book reloads at the new speed.
    this.rateTimer = setTimeout(() => {
      this.rateTimer = null;
      if (this.book && this.hasMedia()) this.reload(this.position(), this.wantPlay);
    }, RATE_DEBOUNCE_MS);
  }

  stop() {
    this.wantPlay = false;
    this.loadSerial++;
    this.loading = false;
    this.book = null;
    this.target = null;
    const m = this.media;
    this.detach();
    m?.stop(new this.api.media.StopRequest(), noop, noop);
  }

  // ------------------------------------------------------------ internals

  private ours(media: CastMedia): boolean {
    const id = media.media?.customData?.plumeItemId;
    return !!id && id === this.itemId;
  }

  private mediaPosition(media: CastMedia): number {
    const start = Number(media.media?.customData?.startOffset ?? 0);
    const t = media.getEstimatedTime();
    return start + (Number.isFinite(t) ? t : 0);
  }

  private attach(media: CastMedia, fromEvent: boolean) {
    const data = media.media?.customData;
    if (!data?.plumeItemId) return;
    // Media loaded by a load of ours still in flight is attached by that load.
    if (fromEvent && this.loading) return;
    if (this.media === media) return;
    this.media?.removeUpdateListener(this.onUpdate);
    this.media = media;
    if (!this.itemId || !this.loading) this.itemId = data.plumeItemId;
    media.addUpdateListener(this.onUpdate);
    if (this.filesFor !== data.plumeItemId) void this.loadFiles(data.plumeItemId).catch(() => undefined);
    this.mediaUpdated(true);
  }

  private detach() {
    this.media?.removeUpdateListener(this.onUpdate);
    this.media = null;
  }

  private mediaUpdated(alive: boolean) {
    const m = this.media;
    if (!m) return;
    if (!this.ours(m)) {
      if (!alive) this.detach();
      return;
    }
    // An idle receiver may report no position (or 0): keep the last real one.
    const idle = m.playerState === 'IDLE';
    const position = idle ? this.last.position : this.mediaPosition(m);
    if (this.target && !this.loading && !idle && (Math.abs(position - this.target.at) < 3 || Date.now() - this.target.since > TARGET_HOLD_MS)) this.target = null;
    if (this.ending) {
      if (!alive) this.detach();
      return;
    }
    this.last = { position: this.target ? this.target.at : position, playing: m.playerState === 'PLAYING' };
    if (this.loading) return;
    const data = m.media?.customData;
    const isLast = !data || data.count == null || Number(data.index) >= Number(data.count) - 1;
    // The SDK also reports the end of the media (finished, failed, stopped elsewhere) as `alive = false`.
    const status = castStatusOf(m, isLast);
    if (status === 'error') {
      if (this.retryAllowed()) {
        console.warn('[cast] receiver error; reloading with a fresh token');
        this.reload(this.last.position, this.wantPlay, true);
        return;
      }
      this.detach();
      this.host.remoteStatus(this, { playing: false, buffering: false, ended: false, error: `${this.deviceName} stopped playing. Check that it can reach your Audiobookshelf server.` });
      return;
    }
    if (status === 'gone') {
      // Between queue items the receiver reports the finished file; the next one follows.
      if (alive && m.idleReason === 'FINISHED') return;
      this.detach();
      this.host.remoteStatus(this, { playing: false, buffering: false, ended: false, error: null });
      return;
    }
    if (!alive) this.detach();
    // Play or pause from the TV's remote or Google Home: later reloads keep that choice.
    if (status.playing) this.wantPlay = true;
    else if (m.playerState === 'PAUSED') this.wantPlay = false;
    if (status.playing) this.retry = { count: 0, since: 0 };
    this.host.remoteStatus(this, status);
  }

  private retryAllowed(): boolean {
    const now = Date.now();
    if (now - this.retry.since > CAST_RETRY_WINDOW_MS) this.retry = { count: 0, since: now };
    return this.retry.count++ < CAST_MAX_RETRIES;
  }

  private commandFailed(what: string, err: unknown) {
    console.warn(`[cast] ${what} failed`, err);
    // The receiver lost the media (it was stopped elsewhere): reload where we were.
    if (what === 'play' && this.book) this.reload(this.position(), true);
  }

  private tokenStale(): boolean {
    return this.tokenExp != null && this.tokenExp - Date.now() < 60_000;
  }

  /** Loads the book again at `at` (another file, a fresh token, a new speed, an error). */
  private reload(at: number, autoplay: boolean, refresh = false) {
    const book = this.book;
    if (!book) return;
    void (async () => {
      if (refresh) await ensureFreshToken(60 * 60_000).catch(() => undefined);
      await this.load({ ...book, startAt: at, autoplay, rate: this.rate });
    })().catch((err: unknown) => {
      this.host.remoteStatus(this, { playing: false, buffering: false, ended: false, error: err instanceof Error ? err.message : `${this.deviceName} couldn't play this book.` });
    });
  }

  private async loadFiles(itemId: string): Promise<CastFile[]> {
    if (this.filesFor === itemId) return this.files;
    const item = await getItem(itemId);
    const files = castFiles(item.media.audioFiles ?? []);
    this.files = files;
    this.filesFor = itemId;
    return files;
  }

  private async buildRequest(book: RemoteBook, at: number, autoplay: boolean): Promise<unknown> {
    if (navigator.onLine === false) throw new Error('Casting needs a connection to your Audiobookshelf server.');
    await ensureFreshToken(15 * 60_000).catch(() => undefined);
    const files = await this.loadFiles(book.itemId).catch(() => {
      throw new Error(`Couldn't reach your Audiobookshelf server to cast this book.`);
    });
    if (files.length === 0) throw new Error('This book has no audio files the TV can play.');
    const account = getSession();
    if (!account) throw new Error('Sign in again to cast.');
    const api = this.api;
    const cover = absoluteUrl(mediaUrl(`/api/items/${book.itemId}/cover`, { width: 800, format: 'jpeg' }));
    const items = files.map((f, index) => {
      const url = absoluteUrl(mediaUrl(audioFilePath(book.itemId, f.ino)));
      const info = new api.media.MediaInfo(url, f.mimeType);
      info.contentUrl = url;
      info.streamType = api.media.StreamType?.BUFFERED ?? MEDIA_NAMESPACE_TYPES.BUFFERED;
      info.duration = f.duration;
      const md = new api.media.MusicTrackMediaMetadata();
      md.title = book.title || 'Audiobook';
      md.albumName = book.title;
      md.artist = book.author;
      md.albumArtist = book.author;
      if (files.length > 1) md.trackNumber = index + 1;
      md.images = [new api.Image(cover)];
      info.metadata = md;
      info.customData = { plumeItemId: book.itemId, startOffset: f.startOffset, index, count: files.length } satisfies CastItemData;
      const item = new api.media.QueueItem(info);
      item.autoplay = true;
      item.preloadTime = 20;
      return item;
    });
    const { index, offset } = locateCastFile(files, at);
    const request = new api.media.LoadRequest(items[index].media);
    request.autoplay = autoplay;
    request.currentTime = offset;
    request.playbackRate = castRate(book.rate);
    if (items.length > 1) {
      request.queueData = new api.media.QueueData(book.itemId, book.title, '', api.media.RepeatMode?.OFF ?? 'REPEAT_OFF', items, index, offset);
    }
    this.tokenExp = tokenExpiresAt(account.accessToken);
    return request;
  }
}

function noop() {}

// ---------------------------------------------------------------- context

let started = false;
let host: RemoteHost | null = null;
let current: CastRemote | null = null;

/** Chromium on a secure origin, not iOS (where every browser is WebKit: AirPlay instead). */
export function castSupported(): boolean {
  if (typeof window === 'undefined' || !window.isSecureContext || platform.isIOS) return false;
  return typeof window.chrome === 'object' && window.chrome !== null;
}

/** Loads the Cast SDK (Chromium only) and hands Cast sessions to `engine`. Idempotent. */
export function initCast(engine: RemoteHost): void {
  if (started || !castSupported()) return;
  started = true;
  host = engine;
  castStore.set({ status: 'loading', device: null });
  window.__onGCastApiAvailable = (available) => {
    if (!available) {
      castStore.set({ status: 'unsupported', device: null });
      return;
    }
    try {
      setUpContext();
    } catch (err) {
      console.warn('[cast] setup failed', err);
      castStore.set({ status: 'unsupported', device: null });
    }
  };
  const script = document.createElement('script');
  script.src = CAST_SDK_URL;
  script.async = true;
  script.onerror = () => castStore.set({ status: 'unsupported', device: null });
  document.head.appendChild(script);
}

function setUpContext() {
  const s = sdk();
  if (!s) throw new Error('Cast SDK missing');
  const { cast, framework } = s;
  const context = framework.CastContext.getInstance();
  context.setOptions({
    receiverApplicationId: cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
    autoJoinPolicy: cast.AutoJoinPolicy.ORIGIN_SCOPED,
    resumeSavedSession: true,
  });
  const showState = (state: string) => {
    const device = current?.deviceName ?? context.getCurrentSession()?.getCastDevice()?.friendlyName ?? null;
    switch (state) {
      case framework.CastState.NO_DEVICES_AVAILABLE:
        castStore.set({ status: 'noDevices', device: null });
        break;
      case framework.CastState.CONNECTING:
        castStore.set({ status: 'connecting', device });
        break;
      case framework.CastState.CONNECTED:
        castStore.set({ status: 'connected', device });
        break;
      default:
        castStore.set({ status: 'available', device: null });
    }
  };
  context.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, (e: { castState: string }) => showState(e.castState));
  context.addEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, (e: { sessionState: string; session?: CastSessionLike }) => {
    const S = framework.SessionState;
    if ((e.sessionState === S.SESSION_STARTED || e.sessionState === S.SESSION_RESUMED) && e.session) {
      startRemote(e.session);
      showState(context.getCastState());
    } else if (e.sessionState === S.SESSION_ENDED || e.sessionState === S.SESSION_START_FAILED) {
      endRemote();
      showState(context.getCastState());
    }
  });
  showState(context.getCastState());
  // A session that survived a reload (resumeSavedSession) may already be up.
  const existing = context.getCurrentSession();
  if (existing) startRemote(existing);
}

function startRemote(session: CastSessionLike) {
  if (!host) return;
  endRemote();
  const remote = new CastRemote(session, host);
  current = remote;
  castStore.set({ status: 'connected', device: remote.deviceName });
  void host.connectRemote(remote).catch((err: unknown) => console.warn('[cast] handover failed', err));
}

function endRemote() {
  const remote = current;
  if (!remote) return;
  current = null;
  remote.markLost();
  host?.disconnectRemote(remote);
  remote.dispose();
}

export type CastRequestResult = 'connected' | 'cancelled' | 'failed';

/** Opens the browser's Cast device picker. Call from a tap. */
export async function requestCast(): Promise<CastRequestResult> {
  const s = sdk();
  if (!s) return 'failed';
  try {
    // Some SDK versions resolve with an error code instead of rejecting.
    const code = await s.framework.CastContext.getInstance().requestSession();
    if (code) throw code;
    return 'connected';
  } catch (err) {
    // The SDK rejects with an error code string; 'cancel' = the picker was dismissed.
    const code = typeof err === 'string' ? err : (err as { code?: string })?.code;
    if (code === 'cancel' || code === s.cast.ErrorCode?.CANCEL) return 'cancelled';
    console.warn('[cast] session request failed', err);
    return 'failed';
  }
}

/** Ends the Cast session (and the receiver app); playback comes back to this device. */
export function stopCasting(): void {
  const s = sdk();
  if (!s) return;
  current?.beginEnding();
  s.framework.CastContext.getInstance().endCurrentSession(true);
}
